import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bot, Check, ChevronDown, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { useHat } from "../../../../context/HatContext";
import { useKnowledgeBase } from "../../../../context/KnowledgeBaseContext";
import { useWorkflow } from "../../../../context/WorkflowContext";
import { useView } from "../../../../context/ViewContext";
import { useUser } from "../../../../context/UserContext";
import { listHats } from "../../../../service/apiService";
import { BrainBankExport } from "../../../../types";

// The VA shape this button needs. Deliberately structural: HatContext and
// types.tsx model `feature_keys` differently, and the two Hat types are not
// assignable to each other -- see VAListView, which casts for the same reason.
type VaOption = {
    id?: string;
    hat_name?: string;
    user_id?: string;
    is_public?: boolean;
    feature_keys?: any;
};

interface AskVirtualAssistantButtonProps {
    /** The BrainBank copies of this meeting's transcript. */
    exports: BrainBankExport[];
    /** Closes the Meetings dialog, so the chat underneath is usable. */
    onClose?: () => void;
}

/**
 * Pick a Virtual Assistant to ask about this meeting.
 *
 * The VA does not have to be attached to the BrainBank first. The chat carries
 * a per-conversation BrainBank alongside the VA -- `knowledge_base_id` next to
 * `hat_id` on the message -- and the server lets that one stand in for the VA's
 * own BrainBanks for those turns. So choosing here pairs the two in the chat
 * that opens, and writes nothing back to the VA: a VA the whole team uses is
 * not silently re-pointed at one meeting's BrainBank.
 *
 * Who is offered follows the rest of the app: an admin sees every VA, everyone
 * else sees the ones they created plus the ones shared with them or that they
 * manage. Org-wide public VAs are left out for a non-admin, so the list stays
 * the handful of assistants that are actually theirs.
 */

/** A VA's attached BrainBank ids. `feature_keys` is a JSON string on older records. */
const attachedKnowledgeBaseIds = (hat: VaOption): string[] => {
    let featureKeys: any = hat?.feature_keys;
    if (typeof featureKeys === "string") {
        try {
            featureKeys = JSON.parse(featureKeys);
        } catch {
            return [];
        }
    }
    const ids = featureKeys?.include_knowledge_base?.knowledge_base_ids;
    return Array.isArray(ids) ? ids.filter((id: unknown): id is string => !!id) : [];
};

const exportedAtMs = (value?: string): number => {
    const parsed = Date.parse(value || "");
    return isNaN(parsed) ? 0 : parsed;
};

const AskVirtualAssistantButton: React.FC<AskVirtualAssistantButtonProps> = ({
    exports,
    onClose,
}) => {
    const { setSelectedHat } = useHat();
    const { setSelectedKnowledgeBase, fetchKnowledgeBasesList } = useKnowledgeBase();
    const { setSelectedWorkflow } = useWorkflow();
    const { setView } = useView();
    const { userId, userDetails } = useUser();

    // The same test useBrainBanks applies, so the two lists agree on who is an
    // admin rather than each deciding for itself.
    const isAdmin = userDetails?.role !== "user";

    // Read straight from the server rather than from HatContext's hatsList:
    // that list is loaded under whichever filter the VA screen last used, and
    // fetchHatsList() silently does nothing while that filter is locked -- so a
    // VA created earlier in the same session can be missing from it.
    const [hats, setHats] = useState<VaOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const searchRef = useRef<HTMLInputElement | null>(null);

    /**
     * The BrainBank the chat is pointed at: the most recently sent one.
     *
     * A meeting can be sent to several BrainBanks, and one BrainBank can hold
     * two records for the same meeting (the transcript document and, when there
     * is one, the imported PDF). A message carries a single BrainBank, so the
     * newest send wins -- which is the one the user just made.
     */
    const targetBank = useMemo(() => {
        let newest: BrainBankExport | null = null;
        for (const record of exports || []) {
            if (!record?.knowledge_base_id) continue;
            if (!newest || exportedAtMs(record.exported_at) >= exportedAtMs(newest.exported_at)) {
                newest = record;
            }
        }
        return newest;
    }, [exports]);

    const assistants = useMemo(() => {
        const visible = hats.filter((hat) => {
            if (!hat?.id) return false;
            // A row with no name cannot be told apart from the next one, so it
            // is not offered as a choice. The other pickers show these as
            // "Unnamed VA"; they are rows in the hats table that carry no
            // hat_name, not assistants anyone can meaningfully pick.
            if (!(hat.hat_name || "").trim()) return false;
            if (isAdmin) return true;
            // list_hats returns shared + own + managed + public merged, with no
            // marker for which bucket a row came from. Anything not public must
            // have come from one of the first three; a public VA is kept only
            // when this user created it.
            return hat.user_id === userId || hat.is_public !== true;
        });
        return visible.sort((a, b) =>
            (a.hat_name || "").localeCompare(b.hat_name || "", undefined, { sensitivity: "base" })
        );
    }, [hats, isAdmin, userId]);

    // What the open menu lists. The selection and the pre-select below read
    // `assistants`, so narrowing the search never drops the chosen VA.
    const matches = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return assistants;
        return assistants.filter((hat) => (hat.hat_name || "").toLowerCase().includes(needle));
    }, [assistants, query]);

    const selected = assistants.find((hat) => hat.id === selectedId) || null;

    const hasTargetBank = !!targetBank;

    useEffect(() => {
        if (!hasTargetBank) return;

        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        listHats()
            .then((result) => {
                if (cancelled) return;
                setHats(Array.isArray(result) ? result : []);
            })
            .catch((err) => {
                console.error("Could not load Virtual Assistants:", err);
                if (!cancelled) setLoadError("Couldn't load Virtual Assistants.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // Once per mount: this dialog is short-lived, and the VA list does not
        // change underneath an open meeting.
    }, [hasTargetBank]);

    // Start on the VA already attached to this BrainBank, when there is one, so
    // the flow that worked before this dropdown existed is still one click. A
    // sole assistant is likewise pre-picked; otherwise the user chooses.
    useEffect(() => {
        if (selectedId || assistants.length === 0 || !targetBank) return;
        const attached = assistants.find((hat) =>
            attachedKnowledgeBaseIds(hat).includes(targetBank.knowledge_base_id)
        );
        const preselect = attached || (assistants.length === 1 ? assistants[0] : null);
        if (preselect?.id) setSelectedId(preselect.id);
    }, [assistants, selectedId, targetBank]);

    // Close the picker on an outside click or on Escape, and start each
    // opening from the full list with the cursor already in the search box.
    useEffect(() => {
        if (!open) {
            setQuery("");
            return;
        }
        searchRef.current?.focus();
        const handleClick = (event: MouseEvent) => {
            if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", handleClick);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open]);

    // Nothing has been sent yet: there is no BrainBank to ask about.
    if (!targetBank) return null;

    const askSelected = () => {
        if (!selected) return;

        setSelectedHat(selected as any);
        // The BrainBank this meeting went to, for this conversation only. It
        // stands in for the VA's own BrainBanks and is never written back.
        setSelectedKnowledgeBase({
            id: targetBank.knowledge_base_id,
            name: targetBank.knowledge_base_name,
            index_name: targetBank.index_name,
        });
        // The chat's BrainBank list may predate this BrainBank (e.g. one created in
        // Send to BrainBank); reload it so the chat box shows it as selected.
        fetchKnowledgeBasesList();
        // VA and workflow are mutually exclusive everywhere else in the app.
        setSelectedWorkflow(null);
        setView("chat");
        setOpen(false);
        toast.success(
            `${selected.hat_name || "Virtual Assistant"} · ${targetBank.knowledge_base_name}`
        );
        onClose?.();
    };

    const controlClass =
        "flex items-center gap-1 whitespace-nowrap text-sm font-medium text-gray-700 border border-gray-300 bg-white hover:bg-gray-50 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50 disabled:hover:bg-white";

    return (
        <div className="flex items-center gap-1.5">
            <div className="relative" ref={wrapperRef}>
                <button
                    type="button"
                    onClick={() => setOpen((value) => !value)}
                    className={controlClass}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    title={`Choose a Virtual Assistant to ask about ${targetBank.knowledge_base_name}`}
                >
                    <Bot className="w-4 h-4 flex-shrink-0" />
                    <span className="max-w-[7rem] sm:max-w-[10rem] truncate">
                        {selected?.hat_name || "Select a VA"}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                </button>

                {open && (
                    <div className="absolute right-0 z-20 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                        <p className="px-3 py-1.5 text-xs text-gray-500 border-b border-gray-100 truncate">
                            Answers from{" "}
                            <span className="font-medium text-gray-700">
                                {targetBank.knowledge_base_name}
                            </span>
                        </p>

                        <div className="px-2 py-1.5">
                            <div className="relative">
                                <input
                                    ref={searchRef}
                                    type="search"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder="Search VA's..."
                                    aria-label="Search Virtual Assistants"
                                    className="w-full px-3 py-1.5 pr-8 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                                />
                                <Search
                                    size={14}
                                    className="absolute right-2.5 top-2 text-gray-400 pointer-events-none"
                                />
                            </div>
                        </div>

                        {loading ? (
                            <p className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500">
                                <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                                Loading Virtual Assistants…
                            </p>
                        ) : loadError ? (
                            <p className="px-3 py-3 text-sm text-gray-600">{loadError}</p>
                        ) : matches.length === 0 ? (
                            <p className="px-3 py-3 text-sm text-gray-500">
                                {query.trim()
                                    ? "No VA's match your search"
                                    : isAdmin
                                    ? "There are no Virtual Assistants yet."
                                    : "You don't have any Virtual Assistants yet."}
                            </p>
                        ) : (
                            <ul
                                role="listbox"
                                aria-label="Virtual Assistants"
                                className="max-h-60 overflow-y-auto"
                            >
                                {matches.map((assistant) => (
                                    <li key={assistant.id}>
                                        <button
                                            type="button"
                                            role="option"
                                            aria-selected={assistant.id === selectedId}
                                            onClick={() => {
                                                setSelectedId(assistant.id || null);
                                                setOpen(false);
                                            }}
                                            className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                        >
                                            <span className="flex-1 min-w-0 truncate">
                                                {assistant.hat_name}
                                            </span>
                                            {assistant.id === selectedId && (
                                                <Check className="w-4 h-4 text-violet-600 flex-shrink-0" />
                                            )}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <button
                type="button"
                onClick={askSelected}
                disabled={!selected}
                className={controlClass}
                title={
                    selected
                        ? `Open the chat with ${selected.hat_name} and ${targetBank.knowledge_base_name}`
                        : "Choose a Virtual Assistant first"
                }
            >
                Ask VA
            </button>
        </div>
    );
};

export default AskVirtualAssistantButton;
